import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { support as supportApi } from '../api/endpoints.ts';
import type { SupportMessage } from '../api/types.ts';
import { errorText, relativeTime, useLang, useStrings } from '../i18n/index.ts';
import { useWatcher } from '../state/watcher.ts';
import { Icon } from '../ui/icons.tsx';
import { cx, EmptyState, ErrorBanner } from '../ui/kit.tsx';
import { Screen } from '../ui/Screen.tsx';

import '../styles/orders.css';

const isMine = (message: SupportMessage) => message.senderRole === 'client';

/** Высота поля растёт с текстом до четырёх строк. */
const MAX_INPUT_HEIGHT = 108;

/**
 * Переписка с поддержкой: по заказу или общая. Сообщения клиента справа, ответы слева; ответ
 * оператора приходит событием ленты. Открытие снимает отметку непрочитанного.
 */
export function SupportScreen({ orderId }: { orderId: string | null }) {
  const strings = useStrings();
  const lang = useLang();
  const { supportVersion } = useWatcher();

  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const stringsRef = useRef(strings);
  stringsRef.current = strings;

  const count = messages.length;
  const countRef = useRef(count);
  countRef.current = count;

  const load = useCallback(async () => {
    try {
      const view = await supportApi.get(orderId);
      setMessages(view.messages);
      setOrderNumber(view.thread?.orderNumber ?? null);
      setError(null);
    } catch {
      // Перечитывание поверх уже показанной переписки молчит: сообщения остаются на экране.
      if (countRef.current === 0) setError(stringsRef.current.somethingWentWrong);
    } finally {
      setIsLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    void load();
  }, [load, supportVersion]);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (count > 0 && scroller) scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
  }, [count]);

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, MAX_INPUT_HEIGHT)}px`;
  }, [text]);

  const body = text.trim();

  const send = async () => {
    if (!body || isSending) return;
    setIsSending(true);
    try {
      const view = await supportApi.send(body, orderId);
      setMessages(view.messages);
      setOrderNumber(view.thread?.orderNumber ?? null);
      setText('');
      setError(null);
    } catch (cause) {
      setError(errorText(strings, cause));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Screen className="support-screen" scrollRef={scrollRef}>
      <h1 className="support-title t-display">{orderNumber ? `${strings.supportTitle} · #${orderNumber}` : strings.supportTitle}</h1>

      <div className="readable gutter stack grow support-messages">
        {messages.length === 0 && !isLoading && !error && (
          <div className="support-empty">
            <EmptyState icon="chat" title={strings.supportEmpty} hint={strings.supportEmptyHint} />
          </div>
        )}
        {messages.map((message) => {
          const mine = isMine(message);
          return (
            <div key={message.id} className={cx('support-bubble', mine && 'is-mine')}>
              <p>{message.text}</p>
              <span className="t-caption t-muted">
                {mine ? '' : `${message.senderName ?? strings.supportStaff} · `}
                {relativeTime(lang, message.createdAt)}
              </span>
            </div>
          );
        })}
        {error && <ErrorBanner text={error} />}
      </div>

      <form
        className="support-composer"
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
      >
        <div className="readable gutter row support-composer-row">
          <textarea
            ref={inputRef}
            rows={1}
            placeholder={strings.supportPlaceholder}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              // С клавиатурой Enter отправляет, Shift+Enter переносит строку; на тач-экране
              // Enter остаётся переносом, отправляет кнопка.
              if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
              if (!window.matchMedia('(pointer: fine)').matches) return;
              event.preventDefault();
              void send();
            }}
          />
          <button type="submit" className="support-send" aria-label={strings.a11ySendMessage} disabled={isSending || !body}>
            <Icon name="send" size={18} weight={2.4} />
          </button>
        </div>
      </form>
    </Screen>
  );
}
